import type { FC } from "react";
import { Alert, Button, Column, Spinner, Text } from "@hightouch/htui";
import {
  AdminEntitlementStatus,
  type EntitlementRequestState,
} from "src/hooks/use-admin-entitlement-request";

interface Props {
  requestState: EntitlementRequestState;
  isLoading: boolean;
  isAwaitingResolution: boolean;
  onRequestAccess: () => void;
  onRefreshStatus: () => void;
}

export const EntitlementRequestAlert: FC<Props> = ({
  requestState,
  isLoading,
  isAwaitingResolution,
  onRequestAccess,
  onRefreshStatus,
}) => {
  const { status, taskId, error } = requestState;

  // Only show full loading alert on initial load (when we don't have a meaningful status yet)
  if (isLoading && status === "idle") {
    return (
      <Alert
        type="info"
        title="Checking entitlement status"
        message={<Spinner size="sm" />}
      />
    );
  }

  if (status === "idle" || status === AdminEntitlementStatus.NotFound) {
    return (
      <Alert
        type="warning"
        title="Admin entitlement required"
        message={
          <Column gap={4}>
            <Text>
              This workspace requires an approved entitlement for admin access.
              Submit a request to get approval.
            </Text>
            <Button
              variant="primary"
              onClick={onRequestAccess}
              isLoading={isLoading}
            >
              Request Admin Access
            </Button>
          </Column>
        }
      />
    );
  }

  if (status === AdminEntitlementStatus.Pending) {
    return (
      <Alert
        type="info"
        title="Entitlement request pending"
        message={
          <Column gap={4}>
            <Text>
              Your request for admin access is pending approval.
              {taskId && (
                <>
                  {" "}
                  Request ID:{" "}
                  <Text as="span" fontWeight="semibold">
                    {taskId}
                  </Text>
                </>
              )}
            </Text>
            {isAwaitingResolution ? (
              <Spinner size="sm" />
            ) : (
              <Button
                variant="secondary"
                onClick={onRefreshStatus}
                isLoading={isLoading}
              >
                Check Status
              </Button>
            )}
          </Column>
        }
      />
    );
  }

  if (status === AdminEntitlementStatus.Approved) {
    return (
      <Alert
        type="success"
        title="Entitlement approved"
        message={
          <Text>
            Your request for admin access has been approved. You can now proceed
            with impersonation.
          </Text>
        }
      />
    );
  }

  if (
    status === AdminEntitlementStatus.Denied ||
    status === AdminEntitlementStatus.Canceled
  ) {
    return (
      <Alert
        type="error"
        title={
          status === AdminEntitlementStatus.Denied
            ? "Entitlement request denied"
            : "Entitlement request canceled"
        }
        message={
          <Column gap={4}>
            <Text>
              Your request for admin access was{" "}
              {status === AdminEntitlementStatus.Denied ? "denied" : "canceled"}
              . Contact your organization administrator for assistance.
            </Text>
            <Button
              variant="secondary"
              onClick={onRequestAccess}
              isLoading={isLoading}
            >
              Submit New Request
            </Button>
          </Column>
        }
      />
    );
  }

  if (status === AdminEntitlementStatus.Error) {
    return (
      <Alert
        type="error"
        title="Error with entitlement request"
        message={
          <Column gap={4}>
            <Text>{error || "An error occurred. Please try again."}</Text>
            <Button
              variant="secondary"
              onClick={onRefreshStatus}
              isLoading={isLoading}
            >
              Retry
            </Button>
          </Column>
        }
      />
    );
  }

  return null;
};
